import { Component, effect, ElementRef, input, signal, ViewChild, model, output } from '@angular/core';
import { NumericOnly } from '../../../directives/numeric-only';
import { parseDate } from '../../../../core/utils/parser';
import { parseNumber } from '../../../../core/utils/parser';

@Component({
  selector: 'date-input-form-control',
  imports: [NumericOnly],
  templateUrl: './date-input-form-control.html',
  styleUrl: './date-input-form-control.scss',
})
export class DateInputFormControl {
  @ViewChild('dayInput') dayInputRef!: ElementRef<HTMLInputElement>;
  @ViewChild('monthInput') monthInputRef!: ElementRef<HTMLInputElement>;
  @ViewChild('yearInput') yearInputRef!: ElementRef<HTMLInputElement>;

  value = model<Date | null>();
  disabled = input<boolean>(false);
  readonly = input<boolean>(false);

  focused = output<void>();
  blurred = output<void>();

  dayNumber = signal<number | null>(null);
  monthNumber = signal<number | null>(null);
  yearNumber = signal<number | null>(null);

  dayText = signal<string | null>(null);
  monthText = signal<string | null>(null);
  yearText = signal<string | null>(null);

  dayFocused = signal<boolean>(false);
  monthFocused = signal<boolean>(false);
  yearFocused = signal<boolean>(false);

  isFocused = signal<boolean>(false);

  private readonly valueEffect = effect(() => {
    const date = parseDate(this.value());

    if (!date) {
      this.dayText.set(null);
      this.monthText.set(null);
      this.yearText.set(null);
      this.dayNumber.set(null);
      this.monthNumber.set(null);
      this.yearNumber.set(null);
      return;
    }

    this.dayText.set(date.getDate().toString().padStart(2, '0'));
    this.monthText.set((date.getMonth() + 1).toString().padStart(2, '0'));
    this.yearText.set(date.getFullYear().toString().padStart(4, '0'));

    this.dayNumber.set(date.getDate());
    this.monthNumber.set(date.getMonth() + 1);
    this.yearNumber.set(date.getFullYear());
  });

  focusDayInput() {
    this.focusInput(this.dayInputRef);
  }

  focusMonthInput() {
    this.focusInput(this.monthInputRef);
  }

  focusYearInput() {
    this.focusInput(this.yearInputRef);
  }

  onDayInput(event: Event) {
    if (this.disabled() || this.readonly()) return;

    const input = event.target as HTMLInputElement;
    const rawValue = input.value;

    const processedValue = this.processTwoDigitInput(rawValue, 3, 31);
    input.value = processedValue;
    this.dayNumber.set(parseNumber(processedValue));
    this.dayText.set(processedValue);

    if (processedValue.length === 2) {
      this.focusMonth();
    }
  }

  onMonthInput(event: Event) {
    if (this.disabled() || this.readonly()) return;

    const input = event.target as HTMLInputElement;
    const rawValue = input.value;
    const processedValue = this.processTwoDigitInput(rawValue, 1, 12);
    input.value = processedValue;
    this.monthNumber.set(parseNumber(processedValue));
    this.monthText.set(processedValue);

    if (processedValue.length === 2) {
      this.focusYear();
    }
  }

  onYearInput(event: Event) {
    if (this.disabled() || this.readonly()) return;

    const input = event.target as HTMLInputElement;
    const rawValue = input.value;
    const processedValue = this.processInputGroup(rawValue, 4);
    input.value = processedValue;
    this.yearNumber.set(parseNumber(processedValue));
    this.yearText.set(processedValue ? processedValue.padStart(4, '0') : null);

    if (processedValue.length === 4) {
      this.yearInputRef?.nativeElement?.blur();
    }
  }

  onDayFocus() {
    this.dayFocused.set(true);
    this.clearInputValue(this.dayInputRef);
  }

  onDayBlur() {
    this.dayFocused.set(false);
    this.commitValue();
  }

  onMonthFocus() {
    this.monthFocused.set(true);
    this.clearInputValue(this.monthInputRef);
  }

  onMonthBlur() {
    this.monthFocused.set(false);
    this.commitValue();
  }

  onYearFocus() {
    this.yearFocused.set(true);
  }

  onYearBlur() {
    this.yearFocused.set(false);
    this.commitValue();
  }

  onDayKeydown(event: KeyboardEvent) {
    this.handleDeleteKeydown(event, this.dayInputRef, () => {
      this.dayText.set(null);
      // this.dayNumber.set(null);
    });
  }

  onMonthKeydown(event: KeyboardEvent) {
    this.handleDeleteKeydown(event, this.monthInputRef, () => {
      this.monthText.set(null);
      // this.monthNumber.set(null);
    });
  }

  private processInputGroup(value: string, groupSize: number): string {
    const remainder: number = value.length % groupSize;
    return remainder === 0 ? value.slice(-groupSize) : value.slice(-remainder);
  }

  private processTwoDigitInput(rawValue: string, autoPadThreshold: number, maxValue: number): string {
    const numeric = rawValue.replace(/[^0-9]/g, '');

    if (numeric === '' || numeric === '00') {
      return '';
    }

    if (numeric.length === 1) {
      const firstDigit = Number(numeric);

      if (firstDigit > autoPadThreshold) {
        return numeric.padStart(2, '0');
      }

      return numeric;
    }

    let value = Number(numeric.substring(0, 2));

    if (isNaN(value)) {
      return '';
    }

    if (value < 1) {
      value = 1;
    }

    if (value > maxValue) {
      value = maxValue;
    }

    return value.toString().padStart(2, '0');
  }

  private commitValue(): void {
    const day = this.dayNumber();
    const month = this.monthNumber();
    const year = this.yearNumber();

    if (day === null || month === null || year === null) {
      this.value.set(null);
      return;
    }

    const date = new Date(year, month - 1, day);
    this.value.set(date);
  }

  private focusInput(inputRef: ElementRef<HTMLInputElement> | undefined): void {
    if (inputRef?.nativeElement && !this.disabled() && !this.readonly()) {
      inputRef.nativeElement.focus();
    }
  }

  private clearInputValue(inputRef: ElementRef<HTMLInputElement> | undefined): void {
    if (inputRef?.nativeElement) {
      inputRef.nativeElement.value = '';
    }
  }

  private handleDeleteKeydown(
    event: KeyboardEvent,
    _inputRef: ElementRef<HTMLInputElement> | undefined,
    resetCallback: () => void
  ): void {
    if (event.key === 'Backspace' || event.key === 'Delete') {
      const input = event.target as HTMLInputElement;
      input.value = '';
      resetCallback();
      event.preventDefault();
    }
  }

  private focusMonth(): void {
    this.monthInputRef?.nativeElement?.focus();
  }

  private focusYear(): void {
    this.yearInputRef?.nativeElement?.focus();
  }

  onContainerFocusIn(): void {
    if (!this.isFocused()) {
      this.isFocused.set(true);
      this.focused.emit();
    }
  }

  onContainerFocusOut(event: FocusEvent): void {
    const currentTarget = event.currentTarget as HTMLElement;
    const relatedTarget = event.relatedTarget as HTMLElement | null;

    if (relatedTarget && currentTarget.contains(relatedTarget)) {
      return;
    }

    this.isFocused.set(false);
    this.blurred.emit();

    this.commitValue();
  }
}
